import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Flag } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  { value: "spam", label: "Spam ou contenu publicitaire" },
  { value: "fraud", label: "Annonce frauduleuse ou trompeuse" },
  { value: "duplicate", label: "Doublon d'une autre annonce" },
  { value: "inappropriate", label: "Contenu inapproprié" },
  { value: "wrong_info", label: "Informations erronées" },
  { value: "other", label: "Autre raison" },
];

export function ReportListingModal({ listingId, listingTitle, open, onClose }) {
  const [reason, setReason] = useState("spam");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/report`, { reason, comment: comment || null });
      if (data.duplicated) {
        toast.info("Vous avez déjà signalé cette annonce, notre équipe l'examine.");
      } else {
        toast.success("Signalement envoyé. Merci, notre équipe va l'examiner.");
      }
      setComment("");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="report-modal" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Flag className="w-5 h-5 text-[#D6202C]" /> Signaler une annonce
          </DialogTitle>
          <DialogDescription className="text-xs">
            "{listingTitle}" — Tous les signalements sont examinés par notre équipe modération.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <label className="text-sm font-medium block">Raison du signalement</label>
          <div className="space-y-2" data-testid="report-reasons">
            {REASONS.map((r) => (
              <label
                key={r.value}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                  reason === r.value
                    ? "border-[#D6202C] bg-[#FEF2F3]"
                    : "border-[#E5E5E5] hover:border-[#D6202C]/40"
                }`}
              >
                <input
                  type="radio"
                  name="report-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  data-testid={`report-reason-${r.value}`}
                  className="accent-[#D6202C]"
                />
                <span className="text-sm">{r.label}</span>
              </label>
            ))}
          </div>

          <label className="text-sm font-medium block pt-2">Commentaire (facultatif)</label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Précisez le contexte si nécessaire (max 500 caractères)"
            maxLength={500}
            rows={3}
            className="rounded-xl"
            data-testid="report-comment-input"
          />

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
              Annuler
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="report-submit-btn"
              className="flex-1 rounded-xl bg-[#D6202C] hover:bg-[#B5181F]"
            >
              {submitting ? "Envoi..." : "Envoyer le signalement"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
